import { useState, type ComponentType, type ReactNode } from "react";
import { Eye, EyeOff, Pencil } from "lucide-react";
import { ActionOverflowMenu, ActionOverflowMenuItem } from "../ui/ActionOverflowMenu.js";

export interface OrgUserRecord {
  id: string;
  userId: string;
  role: string;
  createdAt: string | Date;
  user: {
    id: string;
    email: string;
    name: string;
    fullName?: string | null;
    emailVerified?: boolean;
    banned?: boolean | null;
    role?: string | null;
  };
}

export function UserStatusPills({ user }: { user: OrgUserRecord["user"] & { role?: string } }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="rounded-md bg-[var(--tint-purple-bg)] px-2 py-0.5 text-xs capitalize text-[var(--tint-purple-fg)] dark:bg-violet-500/15 dark:text-violet-300">
        {user.role ?? "member"}
      </span>
      {user.emailVerified ? (
        <span className="rounded-md bg-green-500/10 px-2 py-0.5 text-xs text-green-700 dark:text-green-300">
          Verified
        </span>
      ) : (
        <span className="rounded-md bg-amber-500/10 px-2 py-0.5 text-xs text-amber-800 dark:text-amber-300">
          Unverified
        </span>
      )}
      {user.banned ? (
        <span className="rounded-md bg-red-500/10 px-2 py-0.5 text-xs text-red-700 dark:text-red-300">
          Banned
        </span>
      ) : null}
    </div>
  );
}

export function matchesOrgUserSearch(member: OrgUserRecord, query: string): boolean {
  const normalized = query.trim().toLowerCase();
  if (!normalized) {
    return true;
  }

  return [
    member.user.fullName,
    member.user.name,
    member.user.email,
    member.role,
  ].some((value) => value?.toLowerCase().includes(normalized));
}

interface OrgUserSearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  ariaLabel?: string;
  className?: string;
}

const orgUserSearchClassName =
  "flex h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/60 focus:ring-2 focus:ring-ring/30 disabled:cursor-not-allowed disabled:opacity-50 sm:w-[270px]";

export function OrgUserSearch({
  value,
  onChange,
  placeholder = "Search users…",
  ariaLabel = "Search users",
  className,
}: OrgUserSearchProps) {
  return (
    <input
      type="text"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      aria-label={ariaLabel}
      className={className ? `${orgUserSearchClassName} ${className}` : orgUserSearchClassName}
    />
  );
}

export type OrgUserLinkComponent = ComponentType<{
  to: string;
  className?: string;
  "aria-label"?: string;
  children: ReactNode;
}>;

const rowClassName =
  "flex flex-col gap-3 rounded-lg px-2 py-4 sm:flex-row sm:items-center sm:justify-between";
const clickableRowClassName = `${rowClassName} cursor-pointer transition-colors hover:bg-muted/60`;

function OrgUserRowContent({
  member,
}: {
  member: OrgUserRecord;
}) {
  return (
    <div>
      <p className="font-medium text-foreground">{member.user.fullName || member.user.name}</p>
      <p className="text-sm text-muted-foreground">{member.user.email}</p>
      <div className="mt-2">
        <UserStatusPills user={{ ...member.user, role: member.role }} />
      </div>
    </div>
  );
}

function OrgUserRowActions({
  member,
  onEdit,
}: {
  member: OrgUserRecord;
  onEdit: (member: OrgUserRecord) => void;
}) {
  return (
    <ActionOverflowMenu onClick={(event) => event.stopPropagation()}>
      <ActionOverflowMenuItem
        icon={Pencil}
        label="Edit"
        onClick={() => onEdit(member)}
      />
    </ActionOverflowMenu>
  );
}

export function OrgUserList({
  members,
  detailPath,
  linkComponent: LinkComponent,
  onEdit,
  emptyMessage = "No users found.",
}: {
  members: OrgUserRecord[];
  detailPath?: (member: OrgUserRecord) => string;
  linkComponent?: OrgUserLinkComponent;
  onEdit?: (member: OrgUserRecord) => void;
  emptyMessage?: string;
}) {
  if (!members.length) {
    return <p className="text-sm text-muted-foreground">{emptyMessage}</p>;
  }

  return (
    <div>
      {members.map((member, index) => {
        const managePath = detailPath?.(member);
        const ariaLabel = `Manage ${member.user.email}`;

        return (
          <div key={member.id}>
            {managePath ? (
              <div className={clickableRowClassName}>
                {LinkComponent ? (
                  <LinkComponent to={managePath} className="min-w-0 flex-1" aria-label={ariaLabel}>
                    <OrgUserRowContent member={member} />
                  </LinkComponent>
                ) : (
                  <a href={managePath} className="min-w-0 flex-1" aria-label={ariaLabel}>
                    <OrgUserRowContent member={member} />
                  </a>
                )}
                {onEdit ? <OrgUserRowActions member={member} onEdit={onEdit} /> : null}
              </div>
            ) : (
              <div className={rowClassName}>
                <OrgUserRowContent member={member} />
              </div>
            )}
            {index < members.length - 1 ? (
              <hr className="border-0 border-t border-border" aria-hidden="true" />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export interface CreateOrgUserFormProps {
  id?: string;
  orgRoles: readonly string[];
  memberLimit?: number;
  memberCount?: number;
  layout?: "grid" | "stack";
  className?: string;
  onSubmit: (values: {
    email: string;
    password: string;
    name: string;
    role: string;
  }) => Promise<void>;
  children?: ReactNode;
}

export function CreateOrgUserForm({
  id,
  orgRoles,
  memberLimit,
  memberCount,
  layout = "grid",
  className,
  onSubmit,
  children,
}: CreateOrgUserFormProps) {
  const [passwordVisible, setPasswordVisible] = useState(false);
  const fieldClassName =
    "flex h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/60 focus:ring-2 focus:ring-ring/30 disabled:cursor-not-allowed disabled:opacity-50";
  const selectClassName = `${fieldClassName} cursor-pointer appearance-none bg-[length:1rem] bg-[position:right_0.75rem_center] bg-no-repeat pr-10 [background-image:url('data:image/svg+xml,%3Csvg%20xmlns=%27http://www.w3.org/2000/svg%27%20width=%2716%27%20height=%2716%27%20viewBox=%270%200%2024%2024%27%20fill=%27none%27%20stroke=%27%2364748b%27%20stroke-width=%272%27%20stroke-linecap=%27round%27%20stroke-linejoin=%27round%27%3E%3Cpath%20d=%27m6%209%206%206%206-6%27/%3E%3C/svg%3E')]`;

  const formClassName =
    className ?? (layout === "stack" ? "space-y-4" : "grid gap-4 md:grid-cols-2");

  return (
    <form
      id={id}
      className={formClassName}
      onSubmit={async (event) => {
        event.preventDefault();
        const form = event.currentTarget;
        const data = new FormData(form);
        await onSubmit({
          email: String(data.get("email") ?? ""),
          password: String(data.get("password") ?? ""),
          name: String(data.get("name") ?? ""),
          role: String(data.get("role") ?? "member"),
        });
      }}
    >
      {memberLimit != null && memberCount != null ? (
        <p className="md:col-span-2 text-sm text-muted-foreground">
          Members: {memberCount} / {memberLimit}
        </p>
      ) : null}
      <div className="space-y-1.5">
        <label htmlFor="create-email" className="text-sm text-foreground">
          Email
        </label>
        <input
          id="create-email"
          name="email"
          type="email"
          required
          className={fieldClassName}
        />
      </div>
      <div className="space-y-1.5">
        <label htmlFor="create-name" className="text-sm text-foreground">
          Name
        </label>
        <input
          id="create-name"
          name="name"
          className={fieldClassName}
        />
      </div>
      <div className="space-y-1.5">
        <label htmlFor="create-password" className="text-sm text-foreground">
          Password
        </label>
        <div className="relative">
          <input
            id="create-password"
            name="password"
            type={passwordVisible ? "text" : "password"}
            minLength={8}
            required
            className={`${fieldClassName} pr-10`}
          />
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
            onClick={() => setPasswordVisible((v) => !v)}
            aria-label={passwordVisible ? "Hide password" : "Show password"}
            tabIndex={-1}
          >
            {passwordVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </div>
      <div className="space-y-1.5">
        <label htmlFor="create-role" className="text-sm text-foreground">
          Organization role
        </label>
        <select
          id="create-role"
          name="role"
          defaultValue="member"
          className={selectClassName}
        >
          {orgRoles.map((role) => (
            <option key={role} value={role}>
              {role}
            </option>
          ))}
        </select>
      </div>
      {children}
    </form>
  );
}
